import { useMemo, useState } from 'react'
import { useTransactions } from '@/features/transactions/hooks'
import { useBudgetMonth } from '@/features/budget/hooks'
import { currentYearMonth, stepMonth } from '@/features/budget/format'
import { incomeVsExpense, monthRange, spendingByCategory } from './calc'
import { SpendingDonut } from './SpendingDonut'
import { IncomeExpenseChart } from './IncomeExpenseChart'

/**
 * Reports MVP. Three things on one page:
 *   1. Spending donut for a selected month
 *   2. Income vs expense bar (12-month rolling)
 *   3. Top-line cash flow card for the selected month
 *
 * Date range picker: month-granular only for now. Custom ranges +
 * net-worth line + Sankey + drill-through are explicit follow-ups
 * (WEB-32, WEB-33, WEB-34).
 *
 * Data: reuses the cached transactions query (500-row cap shared
 * across screens). For users with multi-year histories that limit
 * needs lifting before the 12-month chart is trustworthy — tracked
 * with the pagination follow-up.
 */
export function ReportsScreen() {
  const today = currentYearMonth()
  const [active, setActive] = useState(today)
  const txQuery = useTransactions(2000) // higher cap for 12-month aggregates
  const monthQuery = useBudgetMonth(active.year, active.month)

  const range = useMemo(() => monthRange(active.year, active.month), [active])
  const groups = useMemo(
    () => monthQuery.data?.categoryGroups ?? [],
    [monthQuery.data],
  )

  const spending = useMemo(
    () => spendingByCategory(txQuery.data ?? [], monthQuery.data ?? undefined, range, groups),
    [txQuery.data, monthQuery.data, range, groups],
  )

  const monthly = useMemo(
    () => incomeVsExpense(txQuery.data ?? [], active.year, active.month, 12),
    [txQuery.data, active.year, active.month],
  )

  const monthTotals = useMemo(() => {
    const m = monthly.find((r) => {
      const [y, mo] = r.key.split('-').map((n) => parseInt(n, 10))
      return y === active.year && mo === active.month
    })
    return { income: m?.income ?? 0, expenses: m?.expenses ?? 0, net: m?.net ?? 0 }
  }, [monthly, active])

  const spendingTotal = spending.reduce((a, b) => a + b.total, 0)

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Reports</h1>
        <div className="flex items-center gap-2 text-sm" role="group" aria-label="Report month">
          <button
            type="button"
            onClick={() => {
              const next = stepMonth(active.year, active.month, -1)
              setActive({ year: next.year, month: next.month })
            }}
            aria-label="Previous month"
            className="rounded-md border border-zinc-300 px-2 py-1 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            ←
          </button>
          <div className="min-w-[120px] text-center font-medium">{range.label}</div>
          <button
            type="button"
            onClick={() => {
              const next = stepMonth(active.year, active.month, 1)
              setActive({ year: next.year, month: next.month })
            }}
            aria-label="Next month"
            className="rounded-md border border-zinc-300 px-2 py-1 hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
          >
            →
          </button>
        </div>
      </header>

      {txQuery.isLoading ? (
        <div className="rounded-xl border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          Loading…
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <StatCard label="Income" value={monthTotals.income} tone="income" />
            <StatCard label="Expenses" value={monthTotals.expenses} tone="expense" />
            <StatCard
              label="Net cash flow"
              value={monthTotals.net}
              tone={monthTotals.net < 0 ? 'expense' : 'income'}
            />
          </div>

          <section aria-label="Spending by category" className="space-y-2">
            <h2 className="text-sm font-medium">Spending by category — {range.label}</h2>
            <SpendingDonut rows={spending} total={spendingTotal} />
          </section>

          <IncomeExpenseChart rows={monthly} />
        </>
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'income' | 'expense'
}) {
  const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
  const color = tone === 'income' ? 'text-funded' : 'text-overspent'
  return (
    <div className="rounded-xl border border-zinc-200 p-4 dark:border-zinc-800">
      <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${color}`}>
        {usd.format(value)}
      </div>
    </div>
  )
}
